import React from "react";
import { Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { useImage } from "@/hooks/use-image";

type ThumbnailSize = "sm" | "md" | "lg";

const sizeClasses: Record<ThumbnailSize, { container: string; icon: string; spinner: string }> = {
  sm: { container: "w-12 h-12", icon: "h-5 w-5", spinner: "h-4 w-4" },
  md: { container: "w-24 h-24", icon: "h-8 w-8", spinner: "h-6 w-6" },
  lg: { container: "w-full aspect-square", icon: "h-6 w-6", spinner: "h-4 w-4" },
};

interface ProductThumbnailProps {
  imageUrl: string | null | undefined;
  alt?: string;
  size?: ThumbnailSize;
  /** Whether the image should load (for lazy/visibility-gated loading). Defaults to true. */
  isVisible?: boolean;
  className?: string;
}

export const ProductThumbnail: React.FC<ProductThumbnailProps> = ({
  imageUrl: productImageUrl,
  alt,
  size = "md",
  isVisible = true,
  className,
}) => {
  const { imageUrl, imageLoading, imageError } = useImage({
    imageUrl: productImageUrl,
    isVisible,
  });

  const { container, icon, spinner } = sizeClasses[size];

  return (
    <div
      className={cn(
        "rounded-md overflow-hidden flex-shrink-0 bg-muted",
        container,
        className,
      )}
    >
      {imageLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className={cn("animate-spin rounded-full border-b-2 border-blue-600", spinner)} />
        </div>
      ) : imageError || !imageUrl ? (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
          <Package className={icon} />
        </div>
      ) : (
        <img
          src={imageUrl}
          alt={alt}
          className="w-full h-full object-contain"
          loading="lazy"
        />
      )}
    </div>
  );
};
